import { _axios } from '@/plugins/axios'
import router from '@/router/router'
import converter from '@/plugins/converters/questions'
import i18n from '@/plugins/i18n'

export const DEFAULT_WEIGHT = 1

const state = {
  /**
   * @typedef {Object} Skills
   * @property {String|Number} id - ID de la compétence associée
   * @property {String} name - Texte de la compétence associée
   * @property {Number} weight - Pondération de la relation compétence - question
   */
  /**
   * @typedef {Object} LikertQuestionAir
   * @property {Number} id - ID de la question
   * @property {String} label - Label de la question
   * @property {String} text - Texte complet de la question
   * @property {Boolean} isActive - État d'activation de la question
   * @property {Number} createdAt - Date de création de la question
   * @property {Number} updatedAt - Date de modification de la question
   * @property {String} type - Type de la question
   * @property {Boolean} selected - Indique si la question est associée par une compétence
   * @property {ScaleAir} scale - Échelle associée
   * @property {Skills[]} skills - Tableau des compétences associées
   * @property {Number} skillsNumber - Nombre de compétences associées
   * @property {Number} answer - Colonne sélectionnée dans l'échelle de Likert
   * @type {LikertQuestionAir}
   */
  questionLikert: {},
  /**
   * @type {LikertQuestionAir[]}
   */
  questionsLikert: [],
  /**
   * @typedef {Object} OpenQuestionAir
   * @property {Number} id - ID de la question
   * @property {String} label - Label de la question
   * @property {String} text - Texte complet de la question
   * @property {Boolean} isActive - État d'activation de la question
   * @property {Number} createdAt - Date de création de la question
   * @property {Number} updatedAt - Date de modification de la question
   * @property {String} type - Type de la question
   * @property {Boolean} required - La question est-elle obligatoire
   * @property {Boolean} isLong - Type de réponse; Longue ou courte
   * @property {String} answer - Text entré par l'utilisateur
   * @type {OpenQuestionAir}
   */
  questionOpen: {},
  /**
   * @type {OpenQuestionAir[]}
   */
  questionsOpen: [],
  /**
   * @typedef {Object} MCQResponseAir
   * @property {String} id - ID de la réponse
   * @property {String} text - Texte de la réponse
   * @property {String} value - Valeur associée au texte
   */
  /**
   * @typedef {Object} MCQQuestionAir
   * @property {Number} id - ID de la question
   * @property {String} label - Label de la question
   * @property {String} text - Texte complet de la question
   * @property {Boolean} isActive - État d'activation de la question
   * @property {Number} createdAt - Date de création de la question
   * @property {Number} updatedAt - Date de modification de la question
   * @property {String} type - Type de la question
   * @property {Boolean} required - La question est-elle obligatoire
   * @property {Boolean} isUnique - La question est type QCM ou QCU
   * @property {MCQResponseAir[]} responses - Liste des réponses proposées
   * @property {String} answer - Réponses sélectionnés par l'utilisateur
   * @type {MCQQuestionAir}
   */
  questionMCQ: {},
  /**
   * @type {MCQQuestionAir[]}
   */
  questionsMCQ: [],
  /**
   * {(LikertQuestionAir | OpenQuestionAir | MCQQuestionAir)[]}
   */
  questions: [],
  /**
   * @typedef {Object} ScaleAir
   * @property {Number} id - ID de l'échelle
   * @property {String} label - Label de la de l'échelle
   * @property {Boolean} isActive - État d'activation de l'échelle
   * @property {Date} updatedAt - Date de dernière modification de l'échelle
   * @property {String} maxLabel - Étiquette haute de l'échelle
   * @property {Number} minLabel - Étiquette basse de l'échelle
   * @property {Boolean} inverted - État d'invertion de l'échelle
   * @type {ScaleAir[]}
   */
  scales: []
}

const getters = {
  questionLikert: state => state.questionLikert,
  questionsLikert: state => state.questionsLikert,
  questionOpen: state => state.questionOpen,
  questionsOpen: state => state.questionsOpen,
  questionMCQ: state => state.questionMCQ,
  questionsMCQ: state => state.questionsMCQ,
  questions: state => state.questions,
  scales: state => state.scales,
  getQuestionLabelByType: state => type => {
    const question = type === 'likert'
      ? state.questionLikert
      : type === 'open'
        ? state.questionOpen
        : state.questionMCQ
    return question.label
  }
}

const actions = {
  /**
   * Initie un objet question vide pour remplir le formulaire vierge.
   *
   * @param commit
   * @param questionType {String} Type de question à initialiser.
   */
  initQuestion: ({ commit }, questionType) => {
    commit('INIT', questionType)
  },
  /**
   * Envoie une nouvelle question à Django pour sauvegarder en BDD.
   *
   * @param commit
   * @param dispatch
   * @param question {LikertQuestionAir|OpenQuestionAir|MCQQuestionAir} La question à sauver.
   * @param saveType {String} Le type de question pour savoir quelle conversion appliquer.
   * @return {Promise<void>}
   */
  createQuestion: ({ commit, dispatch }, { question, saveType }) => {
    const questionCreated = converter.JSToDJ[question.type](question)

    return _axios.post('questions/', questionCreated)
      .then(response => {
        if (saveType === 'saveNew') {
          commit('INIT', question.type)
        } else if (saveType === 'save') {
          router.push({ name: 'questionsList' })
        } else { // assumed saveType is saveContinue || saveQuestionnaire
          commit('LOAD_ONE_' + question.type.toUpperCase(), response.data)
          commit('ADD_' + question.type.toUpperCase())
          // redirect to modification page
          if (saveType !== 'saveQuestionnaire') {
            const capitalizedType = question.type.charAt(0).toUpperCase() +
              question.type.slice(1)
            router.push({
              name: `question${capitalizedType}Modification`,
              params: { id: response.data.id }
            })
          }
        }
        dispatch('displayInfo',
          i18n.t('store.manager.question.saved', { label: question.label }),
          { root: true })
      })
      .catch(error => {
        dispatch('displayError',
          { action: i18n.t('store.manager.question.saveError'), error },
          { root: true })
      })
  },
  /**
   * Charge les échelles de Likert pour les utiliser avec les questions de Likert.
   *
   * @param commit
   * @param dispatch
   * @return {Promise<void>}
   */
  loadScales: ({ commit, dispatch }) => {
    return _axios.get('likert-scales/')
      .then((response) => {
        commit('LOAD_SCALES', response.data)
      })
      .catch((error) => {
        dispatch('displayLoadError', error, { root: true })
      })
  },
  /**
   * Charge une question unique.
   *
   * @param commit
   * @param dispatch
   * @param questionType {String} Type de question pour savoir la conversion à appliquer.
   * @param id {String|Number} ID de la question à charger.
   * @param isClone {Boolean} indique s'il s'agit d'une duplication de question
   * @return {Promise<void>}
   */
  loadQuestion: ({ commit, dispatch }, { questionType, id, isClone }) => {
    return _axios.get(`questions/${id}/`)
      .then(async (response) => {
        if (isClone) {
          response.data.id = null
          response.data.label = `Copie de ${response.data.label}`
        }
        commit('LOAD_ONE_' + questionType.toUpperCase(), response.data)
      })
      .catch((error) => {
        dispatch('displayLoadError', error, { root: true })
      })
  },
  /**
   * Charge toutes les questions.
   *
   * @param commit
   * @param dispatch
   * @return {Promise<void>}
   */
  loadQuestions: ({ commit, dispatch }) => {
    return _axios.get('questions/')
      .then(async (response) => {
        commit('LOAD_ALL', response.data)
      })
      .catch(error => {
        dispatch('displayLoadError', error, { root: true })
      })
  },
  loadRepositoryQuestions: ({ commit, dispatch, rootGetters }) => {
    const repositoryId = rootGetters['Repositories/repositorySelectedId']
    return _axios.get(`questions/?repository=${repositoryId}&is_active=true`)
      .then(async response => {
        commit('LOAD_ALL', response.data)
        commit('SORT_QUESTIONS')
      })
      .catch(error => {
        dispatch('displayError',
          { action: i18n.t('store.manager.question.repositoryFilter'),
            error },
          { root: true })
      })
  },
  /**
   * Charge toutes les questions d'un type.
   *
   * @param state
   * @param commit
   * @param dispatch
   * @return {Promise<void>}
   */
  loadActiveLikerts: ({ commit, rootGetters, dispatch }) => {
    const repositoryId = rootGetters['Repositories/repositorySelectedId']
    return _axios.get(
      `questions/?question_type=likert&is_active=true&repository=${repositoryId}`
    )
      .then(async (response) => {
        commit('LOAD_LIKERT', response.data)
      })
      .catch(error => {
        dispatch('displayLoadError', error, { root: true })
      })
  },
  loadLikertsOfQuestionnaire: ({ commit, dispatch }, questionnaireId) => {
    return _axios.get(`questions/?questionnaire=${questionnaireId}`)
      .then(response => {
        commit('LOAD_LIKERT', response.data)
      })
      .catch(error => {
        dispatch('displayError',
          { action: i18n.t('store.manager.question.questionnaireFilter'),
            error },
          { root: true })
      })
  },
  /**
   * Envoie une question qui existe à Django pour sauvegarder les modifications éventuelles.
   *
   * @param context
   * @param question {LikertQuestionAir|OpenQuestionAir|MCQQuestionAir} Question à sauver.
   * @param redirect {Boolean} Indique si on doit réorienter l'utilisavteur vers la liste
   *                           des questions
   * @return {Promise<void>}
   */
  saveQuestion: ({ commit, getters, dispatch }, { question, redirect }) => {
    const questionType = question.type
    const label = getters.getQuestionLabelByType(questionType)

    const questionSaved = converter.JSToDJ[questionType](question)

    return _axios.patch(`questions/${question.id}/`, questionSaved)
      .then(response => {
        if (redirect) {
          router.push({ name: 'questionsList' })
        } else {
          commit('LOAD_ONE_' + questionType.toUpperCase(), response.data)
        }
        dispatch('displayInfo',
          i18n.t('store.manager.question.patched', { label }),
          { root: true })
      })
      .catch(error => {
        dispatch('displayError',
          { action: i18n.t('store.manager.question.saveError'), error },
          { root: true })
      })
  }
}

const mutations = {
  ADD_LIKERT: state => {
    state.questionsLikert = [ state.questionLikert, ...state.questionsLikert ]
  },
  ADD_OPEN: state => {
    state.questionsOpen = [ state.questionOpen, ...state.questionsOpen ]
  },
  ADD_MCQ: state => {
    state.questionsMCQ = [ state.questionMCQ, ...state.questionsMCQ ]
  },
  INIT: (state, type) => {
    const baseQuestion = {
      id: null,
      label: '',
      text: '',
      isActive: true,
      createdAt: null,
      updatedAt: null
    }
    switch (type) {
      case 'likert':
        state.questionLikert = {
          ...baseQuestion,
          type: 'likert',
          selectedBySkill: false,
          scale: {
            id: null,
            maxLabel: '',
            minLabel: '',
            inverted: false
          },
          skills: []
        }
        break

      case 'open':
        state.questionOpen = {
          ...baseQuestion,
          type: 'open',
          required: true,
          isLong: false
        }
        break

      case 'mcq':
        state.questionMCQ = {
          ...baseQuestion,
          type: 'mcq',
          required: true,
          isUnique: false,
          responses: [{ id: 0, text: '' }]
        }
        break

      default:
        break
    }
  },
  LOAD_ALL: (state, questions) => {
    state.questions = questions.map(
      question => converter.DJToJS[question.question_type](question))
  },
  /**
   * Utilisé dynamiquement par l'action LoadQuestion.
   *
   * @param state
   * @param question
   * @constructor
   */
  LOAD_ONE_LIKERT: (state, question) => {
    state.questionLikert = converter.DJToJS.likert(question)
  },
  /**
   * Utilisé dynamiquement par l'action LoadQuestion.
   *
   * @param state
   * @param question
   * @constructor
   */
  LOAD_ONE_MCQ: (state, question) => {
    state.questionMCQ = converter.DJToJS.mcq(question)
  },
  /**
   * Utilisé dynamiquement par l'action LoadQuestion.
   *
   * @param state
   * @param question
   * @constructor
   */
  LOAD_ONE_OPEN: (state, question) => {
    state.questionOpen = converter.DJToJS.open(question)
  },
  LOAD_SCALES: (state, scales) => {
    state.scales = scales.map(scale => converter.DJToJS.likertScale(scale, true))
  },
  LOAD_LIKERT: (state, questions) => {
    state.questionsLikert = questions.map(question => converter.DJToJS.likert(question))
  },
  /**
   * Utilisé dynamiquement par l'action LoadQuestion.
   *
   * @param state
   * @param questions
   * @constructor
   */
  LOAD_MCQ: (state, questions) => {
    state.questionsMCQ = questions.map(question => converter.DJToJS.mcq(question))
  },
  /**
   * Utilisé dynamiquement par l'action LoadQuestion.
   *
   * @param state
   * @param questions
   * @constructor
   */
  LOAD_OPEN: (state, questions) => {
    state.questionsOpen = questions.map(question => converter.DJToJS.open(question))
  },
  SORT_QUESTIONS: state => {
    state.questionsLikert = state.questions.filter(
      q => q.type === 'likert'
    )
    state.questionsOpen = state.questions.filter(
      q => q.type === 'open'
    )
    state.questionsMCQ = state.questions.filter(
      q => q.type === 'mcq'
    )
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
